import { useMutation, useQuery } from 'react-query'
import AttToken from '../../services/AttToken'
import Versions from '../../services/version'
import { Button, Skeleton, Stack, TextField, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useMask } from '@react-input/mask';
import { toast } from 'react-toastify'
import queryClient from '../../services/query'
import ModeEditOutlineIcon from '@mui/icons-material/ModeEditOutline';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

const versionService = new Versions();

type versionType = {
  android: string,
  ios: string,
}

export default function SettingsComp() {
  const [version, setVersion] = useState<versionType>({} as versionType)
  const [edit, setEdit] = useState<string>('');
  const [iosVersion, setIosVersion] = useState({
    first: '',
    second: '',
    third: ''
  });

  function checkChar(value: string): undefined | string {
    if (value === '') {
      return ''
    }
    const pattern = /[0-9]/
    if (pattern.test(value)) {
      return value;
    }
    return
  }

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['GET_VERSION'],
    queryFn: async () => {
      const token = await AttToken()
      if (token) {
        const response = await versionService.getVersion(token)
        return response
      }
    },
    keepPreviousData: true,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false
  })

  const { mutate } = useMutation({
    mutationKey: ['SET_VERSION'],
    mutationFn: async (ver: { os: string, versao: string }) => {
      toast.warn(`Alterando versão ${edit}`)
      try {
        const token = await AttToken();
        if (token) {
          const res = await versionService.setVersion(token, ver)
          res && toast.success(`Versão ${edit} alterada`)
          queryClient.resetQueries(['GET_VERSION'])
        }
      } catch (err) {
        toast.error(`Erro ao alterar versão ${edit}`)
        queryClient.resetQueries(['GET_VERSION'])
      }
    },
  })

  useEffect(() => {
    if (data && !isLoading) {
      const cortado = data?.versoes?.ios.split('.')
      setIosVersion({
        first: cortado[0],
        second: cortado[1],
        third: cortado[2]
      })
      setVersion({ ...data.versoes })
      localStorage.setItem('versions', JSON.stringify(data))
    }
  }, [isLoading, isFetching, data])

  useEffect(() => {
    setVersion(prev => prev ? { ...prev, ios: `${iosVersion.first}.${iosVersion.second}.${iosVersion.third}` } : prev)
  }, [iosVersion])

  return (
    <Stack direction='column' alignItems='flex-start' justifyContent='flex-start' width='100%' spacing={2}>
      <Typography variant='h5' fontWeight={600} color='textDisabled' textAlign='center' width='100%' >Configurações de versão</Typography>
      {
        isLoading || isFetching
          ?
          <>
            <Stack direction='row' alignItems='center' justifyContent='flex-start' spacing={1}>
              <Skeleton variant='rounded' height={50} width={220} /><Skeleton variant='rounded' height={45} width={100} />
            </Stack>
            <Stack direction='row' alignItems='center' justifyContent='flex-start' spacing={1}>
              <Skeleton variant='rounded' height={50} width={220} /><Skeleton variant='rounded' height={45} width={100} />
            </Stack>
          </>
          :
          <>
            <Stack direction='column' alignItems='flex-start' justifyContent='flex-start'>
              <Typography>Versão IOS</Typography>
              <Stack direction='row' alignItems='center' justifyContent='center' spacing={1}>
                <Stack direction='row' alignItems='flex-end' justifyContent='center'>
                  <TextField
                    disabled={edit !== 'ios'}
                    type='number'
                    sx={{ width: '100px', input: { textAlign: 'center' } }}
                    inputProps={{ min: 0 }}
                    value={iosVersion.first}
                    onChange={(eve) => setIosVersion(prev => prev ? { ...prev, first: eve.target.value } : prev)} />&nbsp;.&nbsp;
                  <TextField
                    disabled={edit !== 'ios'}
                    type='number'
                    sx={{ width: '100px', input: { textAlign: 'center' } }}
                    inputProps={{ min: 0 }}
                    value={iosVersion.second}
                    onChange={(eve) => setIosVersion(prev => prev ? { ...prev, second: eve.target.value } : prev)} />&nbsp;.&nbsp;
                  <TextField
                    disabled={edit !== 'ios'}
                    type='number'
                    sx={{ width: '100px', input: { textAlign: 'center' } }}
                    inputProps={{ min: 0 }}
                    value={iosVersion.third}
                    onChange={(eve) => setIosVersion(prev => prev ? { ...prev, third: eve.target.value } : prev)} />
                </Stack>
                {edit !== 'ios' && <Button
                  variant='outlined'
                  endIcon={<ModeEditOutlineIcon />}
                  disabled={(edit !== '') || isLoading}
                  onClick={() => setEdit('ios')}
                >
                  Editar
                </Button>}
                {edit === 'ios'
                  && <>
                    <Button
                      variant='outlined'
                      color='error'
                      endIcon={<CancelIcon />}
                      onClick={() => {
                        setEdit('')
                        const ver = localStorage.getItem('versions')
                        if (ver) {
                          const parsed = JSON?.parse(ver)
                          setVersion({ ...version, ios: parsed?.versoes?.ios })
                        }
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant='outlined'
                      color='success'
                      endIcon={<SaveIcon />}
                      onClick={() => {
                        mutate({ os: 'ios', versao: version.ios })
                        setEdit('')
                      }}
                      disabled={version.ios?.length < 5}
                    >
                      Salvar
                    </Button>
                  </>}
              </Stack>
            </Stack>
            <Stack direction='row' alignItems='center' justifyContent='flex-start' spacing={1}>
              <TextField
                inputProps={{ maxLength: 4 }}
                disabled={edit !== 'android'}
                label={'Versão Android'}
                sx={{ width: 'fit-content' }}
                value={version?.android}
                onChange={(event) => {
                  const ver = checkChar(event.target.value)
                  if (ver === undefined) return;
                  setVersion({ ...version, android: ver })
                }}
              />
              {edit !== 'android' && <Button
                variant='outlined'
                endIcon={<ModeEditOutlineIcon />}
                onClick={() => setEdit('android')}
                disabled={(edit !== '') || isLoading}
              >
                Editar
              </Button>}
              {edit === 'android'
                && <>
                  <Button
                    color='error'
                    variant='outlined'
                    endIcon={<CancelIcon />}
                    onClick={() => {
                      setEdit('')
                      const ver = localStorage.getItem('versions')
                      if (ver) {
                        const parsed = JSON?.parse(ver)
                        setVersion({ ...version, android: parsed?.versoes?.android })
                      }
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    endIcon={<SaveIcon />}
                    variant='outlined'
                    color='success'
                    disabled={version.android?.length < 4}
                    onClick={() => {
                      mutate({ os: 'android', versao: version.android })
                      setEdit('')
                    }}
                  >
                    Salvar
                  </Button>
                </>
              }
            </Stack>
          </>}
    </Stack>
  )
}
